<template>
  <div class="ds-password-field">
    <div class="ds-password-labels">
      <label
        class="ds-password-label"
        :for="id"
      >{{ label }}</label>
      <!-- For things that sit on the label row, like a "Forgot password?" link. -->
      <slot name="label-aside" />
    </div>

    <!-- The toggle overlays the input so the field keeps one unbroken border, rather than
         being split by a bootstrap input-group append. -->
    <div class="ds-password">
      <ds-input
        :id="id"
        :value="value"
        :type="visible ? 'text' : 'password'"
        :name="name || id"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :invalid="!!error"
        :aria-describedby="error ? errorId : null"
        @input="$emit('input', $event)"
        @blur="$emit('blur', $event)"
      />
      <button
        type="button"
        class="ds-password-toggle"
        :aria-label="visible ? hideLabel : showLabel"
        @click="visible = !visible"
      >
        <feather-icon :icon="visible ? 'EyeOffIcon' : 'EyeIcon'" />
      </button>
    </div>

    <!-- Rendered up-front so a message added later is announced. -->
    <div
      :id="errorId"
      class="ds-password-error"
      role="alert"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
import DsInput from './DsInput.vue'

// Label, masked input, visibility toggle and error message: the arrangement every auth
// screen needs, in one place instead of copied per page.
export default {
  name: 'DsPasswordInput',
  components: { DsInput },
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    // 'current-password' when signing in, 'new-password' when setting one.
    autocomplete: {
      type: String,
      default: 'current-password',
    },
    error: {
      type: String,
      default: '',
    },
    // Passed in so translated pages can localise the toggle.
    showLabel: {
      type: String,
      default: 'Show password',
    },
    hideLabel: {
      type: String,
      default: 'Hide password',
    },
  },
  data() {
    return { visible: false }
  },
  computed: {
    errorId() {
      return `${this.id}-error`
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.ds-password-field {
  margin-bottom: 16px;
}

.ds-password-labels {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  ::v-deep a {
    font-size: 12px;
    font-weight: 600;
    color: $c-orange;
  }
}

.ds-password-label {
  display: block;
  margin: 0 0 5px;
  font-size: 13px;
  font-weight: 600;
  color: $c-text;
}

.ds-password {
  position: relative;

  ::v-deep .ds-input {
    padding-right: 42px;
  }
}

.ds-password-toggle {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  padding: 6px;
  border: none;
  background: none;
  color: $c-text-ter;
  cursor: pointer;

  &:hover { color: $c-text-sec; }

  // Buttons ring on :focus-visible only, per the convention in _mixins.scss.
  &:focus { outline: none; }

  &:focus-visible {
    color: $c-orange;
    // Colour alone is not an indicator, so it gets the ring too.
    box-shadow: 0 0 0 3px rgba($c-orange, 0.14);
    border-radius: 6px;
  }
}

.ds-password-error {
  min-height: 16px;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: $c-danger-text;
}
</style>
